/*
 * Copyright (c) 2024 Pegasystems Inc.
 * All rights reserved.
 */

import { states, OperatorHandlers } from './definitions';
import type { AST, GrammarList, Token } from './types';

/**
 * [Parser]
 * Description : Parses the tokens and builds the AST
 * @class
 */

class Parser {
  public readonly _grammar: GrammarList;
  public _state: string;
  public _tree!: AST;
  public _exprStr: string;
  public readonly _endState: { [key: string]: string };
  public _subParser: Parser | null;
  public _position!: AST;

  /**
   * Description : Parser
   * @class
   * @param grammar     Supported grammar.
   * @param subexpr     SubExpression for paranthesis type expressions.
   * @param endState    States where the sub expression can end.
   */
  constructor(grammar: GrammarList, subexpr?: string, endState?: { [key: string]: string }) {
    this._grammar = grammar;
    this._state = 'expectOperand';
    this._exprStr = subexpr || '';
    this._endState = endState || {};
    this._subParser = null;
  }

  /**
   * [_setParent]
   * Description : Sets the parent for a node.
   * @static
   * @param node        Node for which parent has to be set.
   * @param parent      Parent node
   */
  static _setParent(node: AST, parent: AST) {
    Object.defineProperty(node, '_parent', {
      value: parent,
      writable: true
    });
  }

  /**
   * [_addToken]
   * Description : Processes each token and sets the corresponding state and adds the token to the tree.
   * @private
   * @param token       Token object
   */
  _addToken(token: Token) {
    const state = states[this._state];
    const startExpr = this._exprStr;
    this._exprStr += token.element;
    if (state.subHandler) {
      if (!this._subParser) {
        this._startSubExpression(startExpr);
      }
      const stopState = this._subParser?._addToken(token);
      if (stopState) {
        this._endSubExpression();
        this._state = stopState;
      }
    } else if (state.operatorTypes?.[token.tokenType]) {
      const typeOpts = state.operatorTypes[token.tokenType];
      const handleFunc = OperatorHandlers[token.tokenType];
      if (handleFunc) {
        handleFunc.call(this, token);
      }
      if (typeOpts.toState) {
        this._state = typeOpts.toState;
      }
    } else if (this._endState[token.tokenType]) {
      return this._endState[token.tokenType];
    } else {
      throw new Error(`Token ${token.element} (${token.tokenType}) unexpected in expression: ${this._exprStr}`);
    }
    return false;
  }

  /**
   * [addTokens]
   * Description : Adds all the tokens to the tree.
   * @param tokens List of all tokens of the expression string
   */
  addTokens(tokens: Token[]) {
    tokens.forEach(this._addToken, this);
  }

  /**
   * [buildAst]
   * Description : Resturns the AST.
   * @returns Returns the tree
   */
  buildAst(): AST | null {
    if (this._subParser) {
      this._endSubExpression();
    }
    this._state = 'complete';
    return this._position ? this._tree : null;
  }

  /**
   * [_startSubExpression]
   * Description : Starts a sub expression and creates its corresponding parser.
   * @private
   * @param exprStr Subexpression
   */
  _startSubExpression(exprStr: string) {
    const { endStates } = states[this._state];
    this._subParser = new Parser(this._grammar, exprStr, endStates);
  }

  /**
   * [_endSubExpression]
   * Description : Ends the sub expression and sets it parser to null.
   * @private
   */
  _endSubExpression() {
    const subHandler = states[this._state].subHandler as Function;
    subHandler.call(this, (this._subParser as Parser).buildAst());
    this._subParser = null;
  }

  /**
   * [setPositionInAST]
   * Description : Sets the current position at a specific node in the tree.
   * @param node node where the position has to be set in the tree.
   */
  setPositionInAST(node: AST) {
    if (!this._position) {
      this._tree = node;
    } else {
      this._position.right = node;
      Parser._setParent(node, this._position);
    }
    this._position = node;
  }
}

export default Parser;
